import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface DialogProps {
  open: boolean;
  onClose(): void;
  title: string;
  description?: string | undefined;
  children: ReactNode;
  className?: string | undefined;
}

/**
 * Native <dialog> wrapper. Built-in keyboard handling (Esc closes), focus
 * trapping, and backdrop. We control open/close imperatively via showModal /
 * close to keep React state as source of truth.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  className,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  // Sync the native close event back to React (Esc key, form submit, etc.)
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handler = () => onClose();
    el.addEventListener('close', handler);
    return () => el.removeEventListener('close', handler);
  }, [onClose]);

  return (
    <dialog
      ref={ref}
      onClick={(e) => {
        // Close on backdrop click — the dialog itself doesn't fill its own box,
        // so a click on the backdrop targets the <dialog> element directly.
        if (e.target === ref.current) onClose();
      }}
      className={cn(
        'rounded-[12px] border border-breaker-line shadow-lg bg-white p-0 max-w-md w-full',
        'backdrop:bg-breaker-ink/40 backdrop:backdrop-blur-sm',
        className,
      )}
    >
      <div className="px-5 py-4 border-b border-breaker-line">
        <h2 className="text-base font-semibold text-breaker-ink">{title}</h2>
        {description ? (
          <p className="text-sm text-breaker-mute mt-1">{description}</p>
        ) : null}
      </div>
      <div className="px-5 py-4">{children}</div>
    </dialog>
  );
}
